import './InvoicesPanel.pcss';
import React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Link } from 'react-router-dom';
import { StoreState } from '@mm/state/store-interfaces';
import TableGrid, {
  DataItem,
} from '../../../../../common/components/TableGrid/TableGrid';
import { GridCell } from '../../../../../common/components/Grid/Grid';
import {
  FormattedInvoice,
  SubscriptionAccount,
} from '@mm/state/interfaces/subscription';
import {
  formattedInvoicesSelector,
  selectAccountDetails,
} from '@mm/state/selectors/subscriptionSelectors';
import creditCardIcon from 'assets/icons/credit-card.svg';
import Button from '../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import { SettingsRoutes } from '../../../../../routes/settings-routes';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { Dispatch } from 'redux';
import SubscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';

type OuterProps = {};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const renderInvoiceNumberCell = (invoice: FormattedInvoice) => (
  <GridCell>
    <a href={invoice.invoiceLink} target="_blank">
      #{invoice.invoiceNumber}
    </a>
  </GridCell>
);

const renderTotalCell = (invoice: FormattedInvoice) => {
  const classList = classnames('invoices-panel__total-cell', {
    'invoices-panel--is-refund': invoice.totalCost < 0,
  });

  return (
    <GridCell className={classList}>{invoice.formattedTotalCost}</GridCell>
  );
};

const invoiceGridData: DataItem<FormattedInvoice>[] = [
  {
    label: 'Invoice',
    keyName: 'invoiceNumber',
    renderCell: renderInvoiceNumberCell,
  },
  {
    label: 'Posted On',
    keyName: 'createdAt',
  },
  {
    label: 'Due On',
    keyName: 'dueOn',
  },
  {
    label: 'Status',
    keyName: 'status',
  },
  {
    label: 'Total',
    keyName: 'formattedTotalCost',
    renderCell: renderTotalCell,
  },
];

const getExpiration = (account: SubscriptionAccount | null) => {
  if (account) {
    const month =
      String(account.month).length === 1 ? '0' + account.month : account.month;

    return `${month}/${account.year}`;
  }

  return '';
};

const InvoicesPanel = (props: Props) => {
  const { invoices, accountDetails } = props;
  const account = accountDetails ? accountDetails.account : null;
  const lastCardNumbers = account ? account.lastCardNumbers : 'XXXX';
  const editBillingLink = account ? account.editBillingLink : '';

  return (
    <div className="invoices-panel">
      <div className="invoices-panel__title">
        <span>Your Payments</span>

        <Link
          className="invoices-panel__done-button-link"
          to={SettingsRoutes.billing.getLink()}
        >
          <PrimaryButton className="invoices-panel__done-button">
            Back
          </PrimaryButton>
        </Link>
      </div>

      <div className="invoices-panel__invoices-card-wrapper">
        <div className="invoices-panel__table-title">Your Card</div>

        <div className="invoices-panel__card-wrapper">
          <div className="invoices-panel__card-left-side">
            <img
              src={creditCardIcon}
              className="invoices-panel__card-image"
              alt=""
            />

            <div className="invoices-panel__card-number">
              XXXX XXXX XXXX {lastCardNumbers}
            </div>

            <div className="invoices-panel__expiration">
              {getExpiration(account)}
            </div>
          </div>

          {editBillingLink && (
            <a
              href={editBillingLink}
              target="_blank"
              className="invoices-panel__edit-card-link"
            >
              <Button
                darkGray
                invert
                className="invoices-panel__edit-card-button"
              >
                Edit
              </Button>
            </a>
          )}
        </div>
      </div>

      <div className="invoices-panel__invoices-table-wrapper">
        <div className="invoices-panel__table-title">Invoices</div>

        <TableGrid
          className="invoices-panel__table"
          items={invoices}
          data={invoiceGridData}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  company: activeCompanySelector(state),
  invoices: formattedInvoicesSelector(state),
  accountDetails: selectAccountDetails(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getAccountData: (companyId: number, signal?: AbortSignal) =>
    dispatch(SubscriptionCRUDActions.getAccountData(companyId, signal)),
  clearAccountData: () => dispatch(SubscriptionCRUDActions.clearAccountData()),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(InvoicesPanel);
